import React, { useMemo } from 'react';
import { Box, Button, Chip, Typography } from '@mui/material';
import { Inventory2Outlined as InventoryIcon, OpenInNew } from '@mui/icons-material';
import { CellContext, ColumnDef } from '@tanstack/react-table';
import { Category, InventoryMode, Item, ItemType } from '../../types';
import { formatCurrency } from '../../utils/currency';
import ItemActionsMenu from './ItemActionsMenu';
import { getInventoryMode, tracksInventory } from '../../utils/inventoryMode';
import IdentityAvatar from '../common/IdentityAvatar';
import CompanyNameCell from '../common/CompanyNameCell';

interface ItemColumnsProps {
  translate: (key: string) => string;
  canWrite: boolean;
  isSuperAdmin: boolean;
  onEdit: (item: Item) => void;
  onDelete: (item: Item) => void;
  onManageInventory: (item: Item) => void;
  onCategoryFilter?: (categoryId: string) => void;
  activeCategoryId?: string;
  batchTrackingEnabled?: boolean;
}

export const getItemPriceLabel = (item: Item, translate: (key: string) => string): string => {
  if (item.itemType === ItemType.SERVICE) {
    return translate('items.servicePriceAtSale');
  }
  if (getInventoryMode(item) === InventoryMode.BATCHED) {
    return translate('items.priceVariesByBatch');
  }

  return item.priceGross != null
    ? formatCurrency(item.priceGross)
    : item.price != null ? formatCurrency(item.price) : '-';
};

/** Keeps the table's visual/domain mapping out of the list container. */
export const useItemColumns = ({
  translate: t,
  canWrite,
  isSuperAdmin,
  onEdit,
  onDelete,
  onManageInventory,
  onCategoryFilter,
  activeCategoryId,
  batchTrackingEnabled,
}: ItemColumnsProps): ColumnDef<Item>[] => useMemo(() => {
  const columns: ColumnDef<Item>[] = [
    {
      accessorKey: 'name',
      header: t('items.name'),
      cell: ({ row }: CellContext<Item, unknown>) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 180 }}>
          <IdentityAvatar><InventoryIcon fontSize="small" /></IdentityAvatar>
          <Box sx={{ minWidth: 0 }}><Typography variant="body2" fontWeight={700}>{row.original.name}</Typography><Typography variant="caption" color="text.secondary">{row.original.itemNumber}</Typography></Box>
        </Box>
      ),
    },
    {
      accessorKey: 'price',
      header: t('items.salePrice'),
      cell: ({ row }: CellContext<Item, unknown>) => {
        const item = row.original;
        return (
          <Box>
            <Typography variant="body2" fontWeight="medium">
              {getItemPriceLabel(item, t)}
            </Typography>
          </Box>
        );
      },
    },
    {
      id: 'stock',
      accessorKey: 'totalStockQuantity',
      header: t('inventory.stock'),
      cell: ({ row }: CellContext<Item, unknown>) => {
        const item = row.original;
        const inventoryMode = getInventoryMode(item);
        const tracksStock = inventoryMode !== InventoryMode.NONE;
        const isOutOfStock = tracksStock && item.totalStockQuantity <= 0;
        const isLowStock = tracksStock && item.totalStockQuantity <= 10 && item.totalStockQuantity > 0;
        return (
          <Box>
            <Chip label={!tracksStock ? t('items.inventoryNotTracked') : `${item.totalStockQuantity} ${t('inventory.units')}`} color={!tracksStock ? 'default' : isOutOfStock ? 'error' : isLowStock ? 'warning' : 'success'} size="small" variant="outlined" sx={{ fontWeight: 700, bgcolor: !tracksStock ? 'action.hover' : isOutOfStock ? 'error.50' : isLowStock ? 'warning.50' : 'success.50' }} />
            {batchTrackingEnabled !== false && inventoryMode === InventoryMode.BATCHED && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                {t('inventory.batchTracked')}
              </Typography>
            )}
          </Box>
        );
      },
    },
    {
      accessorKey: 'categories',
      header: t('category.title'),
      enableSorting: false,
      cell: ({ row }: CellContext<Item, unknown>) => (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxWidth: 200 }}>
          {row.original.categories?.map((category: Category) => (
            <Chip key={category.id} label={category.name} size="small" variant="outlined" onClick={onCategoryFilter ? () => onCategoryFilter(category.id) : undefined} aria-pressed={onCategoryFilter ? activeCategoryId === category.id : undefined} sx={{ borderColor: category.color || 'divider', bgcolor: activeCategoryId === category.id ? 'action.selected' : category.color ? `${category.color}18` : 'transparent' }} />
          ))}
        </Box>
      ),
    },
  ];

  if (isSuperAdmin) {
    columns.push({
      accessorKey: 'companyName',
      header: t('items.company'),
      enableSorting: false,
      cell: ({ row }: CellContext<Item, unknown>) => (
        <CompanyNameCell name={row.original.companyName || row.original.company?.name} />
      ),
    });
  }

  columns.push({
    id: 'actions',
    header: t('common.actions'),
    cell: ({ row }: CellContext<Item, unknown>) => (
      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', justifyContent: 'flex-end' }}>
        {tracksInventory(row.original) && <Button size="small" onClick={() => onManageInventory(row.original)} endIcon={<OpenInNew sx={{ fontSize: 15 }} />} sx={{ whiteSpace: 'nowrap' }}>{t('items.openInventory')}</Button>}
        {canWrite && <ItemActionsMenu item={row.original} onEdit={onEdit} onDelete={onDelete} />}
      </Box>
    ),
  });
  return columns;
}, [t, canWrite, isSuperAdmin, onEdit, onDelete, onManageInventory, onCategoryFilter, activeCategoryId, batchTrackingEnabled]);
